import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { resolveDocumentUpdatedAt } from '#/modules/imsMgmt/receipts/shared/resolveDocumentUpdatedAt';
import { toast } from '#/utils/toast';

/**
 * 列表 SideMsg workflow：confirm → mutation → invalidate／更新選中列。
 * 退回改 AppDialog 必填原因（見 DocumentRejectReasonDialog）。
 *
 * @param {object} options
 * @param {object|null} options.row
 * @param {Function} options.setSelectedData
 * @param {object} options.confirmCopy — submit / approve / reject / cancel
 * @param {(status: string) => boolean} options.canSubmit
 * @param {(status: string) => boolean} options.canApprove
 * @param {(status: string) => boolean} options.canReject
 * @param {(status: string) => boolean} options.canCancel
 * @param {object} options.apis — { submit, approve, reject, cancel, fetchDetail }
 * @param {() => unknown[]} options.listQueryKeyPrefix
 * @param {(id: string) => unknown[]} options.detailQueryKey
 * @param {(error: unknown) => string} options.getErrorMessage
 * @param {(error: unknown) => boolean} options.isVersionConflict
 */
export function useDocumentSideMsgWorkflow({
  row,
  setSelectedData,
  confirmCopy,
  canSubmit,
  canApprove,
  canReject,
  canCancel,
  apis,
  listQueryKeyPrefix,
  detailQueryKey,
  getErrorMessage,
  isVersionConflict,
}) {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const id = row?.id;
  const status = row?.status;

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'submit') {
        return apis.submit(id, payload);
      }
      if (action === 'approve') {
        return apis.approve(id, payload);
      }
      if (action === 'reject') {
        return apis.reject(id, payload);
      }
      if (action === 'cancel') {
        return apis.cancel(id);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');

      if (variables.action === 'reject') {
        setRejectDialogOpen(false);
      }

      if (variables.action === 'cancel') {
        await queryClient.invalidateQueries({ queryKey: listQueryKeyPrefix() });
        setSelectedData(null);
        return;
      }

      await queryClient.invalidateQueries({ queryKey: listQueryKeyPrefix() });
      if (id) {
        await queryClient.invalidateQueries({ queryKey: detailQueryKey(id) });
      }

      const next = result?.data;
      if (next && typeof setSelectedData === 'function') {
        setSelectedData({ ...row, ...next });
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
      if (isVersionConflict(error)) {
        toast.info('請重新點選列表列以載入最新資料');
      }
    },
  });

  const resolveUpdatedAt = useCallback(async () => {
    return resolveDocumentUpdatedAt(row, {
      fetchDetail: apis.fetchDetail,
      setSelectedData,
    });
  }, [apis.fetchDetail, row, setSelectedData]);

  const handleSubmit = useCallback(async () => {
    const copy = confirmCopy.submit;
    const ok = await confirm({
      title: copy.title,
      message: copy.message,
      confirmText: copy.confirmText || '提交',
    });
    if (!ok) {
      return;
    }
    const updatedAt = await resolveUpdatedAt();
    if (!updatedAt) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt } });
  }, [commandMutation, confirm, confirmCopy.submit, resolveUpdatedAt]);

  const handleApprove = useCallback(async () => {
    const copy = confirmCopy.approve;
    const ok = await confirm({
      title: copy.title,
      message: copy.message,
      confirmText: copy.confirmText || '核准',
    });
    if (!ok) {
      return;
    }
    const updatedAt = await resolveUpdatedAt();
    if (!updatedAt) {
      return;
    }
    commandMutation.mutate({ action: 'approve', payload: { updatedAt } });
  }, [commandMutation, confirm, confirmCopy.approve, resolveUpdatedAt]);

  const openRejectDialog = useCallback(() => {
    setRejectDialogOpen(true);
  }, []);

  const handleRejectConfirm = useCallback(async (rejectReason) => {
    const updatedAt = await resolveUpdatedAt();
    if (!updatedAt) {
      return;
    }
    commandMutation.mutate({
      action: 'reject',
      payload: { updatedAt, rejectReason },
    });
  }, [commandMutation, resolveUpdatedAt]);

  const handleCancel = useCallback(async () => {
    const copy = confirmCopy.cancel;
    const ok = await confirm({
      title: copy.title,
      message: copy.message,
      confirmText: copy.confirmText || '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'cancel' });
  }, [commandMutation, confirm, confirmCopy.cancel]);

  function RejectDialogHost() {
    const copy = confirmCopy.reject;
    return (
      <DocumentRejectReasonDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        title={copy.title}
        description={copy.message}
        confirmText={copy.confirmText || '退回'}
        isPending={commandMutation.isPending}
        onConfirm={handleRejectConfirm}
      />
    );
  }

  return {
    ConfirmDialogHost,
    RejectDialogHost,
    canSubmit: Boolean(status && canSubmit(status)),
    canApprove: Boolean(status && canApprove(status)),
    canReject: Boolean(status && canReject(status)),
    canCancel: Boolean(status && canCancel(status)),
    isPending: commandMutation.isPending,
    handleSubmit,
    handleApprove,
    handleReject: openRejectDialog,
    handleCancel,
  };
}
